import React, { useState, useEffect, useCallback } from "react";
import axiosClient from "../axiosClient";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useStateContext } from "../context/ContextProvider";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Paper,
  Button,
} from "@mui/material";

const Reports = () => {
  const [data, setData] = useState([]);
  const [month, setMonth] = useState("");
  const [week, setWeek] = useState("");
  const [year, setYear] = useState("");
  const [yearError, setYearError] = useState(""); // State for year validation error
  const { user } = useStateContext();
  const { t } = useTranslation();

  const getMondayOfWeek = (dateString) => {
    try {
      const date = new Date(dateString);
      const day = date.getDay();
      const diff = day === 0 ? -6 : -day + 1;
      date.setDate(date.getDate() + diff);
      return date.toISOString().split("T")[0];
    } catch (error) {
      console.error("Error in getMondayOfWeek:", error);
      return "";
    }
  };

  const validateYear = useCallback(
    (year) => {
      if (!year) return true;
      const yearNum = parseInt(year, 10);
      if (isNaN(yearNum) || yearNum < 2000 || yearNum > 2100) {
        setYearError(t("reports.yearValidationError"));
        return false;
      }
      setYearError("");
      return true;
    },
    [t]
  );

  useEffect(() => {
    if (month) {
      const [year, mon] = month.split("-");
      axiosClient
        .get(`api/appointmentStats/month/${mon}-${year}/`, {
          headers: { Authorization: `Bearer ${user.token}` },
        })
        .then((response) => {
          setData(response.data);
        })
        .catch((error) => {
          console.error("Error fetching month data:", error);
          setData([]);
        });
    }
  }, [month, user.token]);

  useEffect(() => {
    if (week) {
      axiosClient
        .get(`api/appointmentStats/week/${week}/`, {
          headers: { Authorization: `Bearer ${user.token}` },
        })
        .then((response) => {
          setData(response.data);
        })
        .catch((error) => {
          console.error("Error fetching week data:", error);
          setData([]);
        });
    }
  }, [week, user.token]);

  useEffect(() => {
    if (year && validateYear(year)) {
      axiosClient
        .get(`api/appointmentStats/year/${year}/`, {
          headers: { Authorization: `Bearer ${user.token}` },
        })
        .then((response) => {
          setData(response.data);
        })
        .catch((error) => {
          console.error("Error fetching year data:", error);
          setData([]);
        });
    }
  }, [year, user.token, validateYear]);

  const handleMonthChange = (e) => {
    setMonth(e.target.value);
    setYear("");
    setWeek("");
  };

  const handleYearChange = (e) => {
    const value = e.target.value;
    setYear(value);
    setMonth("");
    setWeek("");
    validateYear(value);
  };

  const handleWeekChange = (e) => {
    const selectedDate = e.target.value;
    if (selectedDate) {
      const mondayDate = getMondayOfWeek(selectedDate);
      setWeek(mondayDate);
    } else {
      setWeek("");
    }
    setMonth("");
    setYear("");
  };

  // Clear all filters
  const handleClearFilters = () => {
    setMonth("");
    setYear("");
    setWeek("");
    setYearError("");
    setData([]);
  };

  const CustomTick = ({ x, y, payload }) => {
    const nameParts = payload.value ? payload.value.split(" ") : ["No", "Name"];
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(" ");

    return (
      <g transform={`translate(${x},${y}) rotate(-15)`}>
        <text x={0} y={0} dy={16} textAnchor="end" fill="#666" fontSize="12">
          <tspan x="0" dy="0">
            {firstName}
          </tspan>
          <tspan x="0" dy="1.2em">
            {lastName}
          </tspan>
        </text>
      </g>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Inform users about Monday requirement */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("reports.weekNote")}
      </Typography>

      {/* Input Fields */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              {t("reports.statsByMonth")}
            </Typography>
            <TextField
              type="month"
              value={month}
              onChange={handleMonthChange}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              {t("reports.statsByWeek")}
            </Typography>
            <TextField
              type="date"
              value={week}
              onChange={handleWeekChange}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              {t("reports.statsByYear")}
            </Typography>
            <TextField
              type="number"
              value={year}
              onChange={handleYearChange}
              fullWidth
              variant="outlined"
              InputProps={{
                inputProps: { min: 2000, max: 2100, step: 1 },
              }}
              InputLabelProps={{ shrink: true }}
              error={!!yearError}
              helperText={yearError}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClearFilters}
              sx={{ mt: 2 }}
            >
              {t("common.clearFilters")}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Chart */}
      <Paper elevation={3} sx={{ p: 3 }}>
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={data}
              margin={{ top: 50, right: 10, left: 10, bottom: 70 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="name"
                interval={0}
                tick={<CustomTick />}
                hide={false}
              />
              <YAxis />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #ccc",
                }}
                itemStyle={{ color: "#8884d8" }}
                cursor={{ fill: "rgba(0, 0, 0, 0.1)" }}
              />
              <Legend verticalAlign="top" align="center" iconType="circle" />
              <defs>
                <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#8884d8" stopOpacity={0.3} />
                </linearGradient>
              </defs>
              <Bar
                dataKey="total"
                fill="url(#colorUv)"
                barSize={Math.max(30, 60 / (data.length || 1))}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography textAlign="center">
            {t("reports.noDataForPeriod")}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default Reports;
